/**
 * React Query hooks for reseed events.
 * React Query hooks for the corresponding entity.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "common/queryKeys";
import { fetchApi } from "common/utils/Api";
import {
  FIELD_RESEEDED_EVENT_REJECTION_PERIOD_DAYS,
  RESEED_TOTAL_AMOUNT_UNIT,
  RESEED_TYPE,
  ReseedEvent,
  reseedApi as reseedEventApi,
  ReseedEventBase,
  SEEDING_METHOD,
} from "./reseedEventTypes";

// Re-export types for convenience
export {
  FIELD_RESEEDED_EVENT_REJECTION_PERIOD_DAYS,
  RESEED_TOTAL_AMOUNT_UNIT,
  RESEED_TYPE,
  reseedEventApi,
  SEEDING_METHOD,
};
export type { ReseedEvent, ReseedEventBase };

// Event data changes infrequently after creation - use 5 minute stale time
// to reduce unnecessary refetches while keeping data reasonably fresh
const EVENT_STALE_TIME = 5 * 60 * 1000; // 5 minutes

/**
 * Fetch all reseed events for a farm
 */
export function useReseedEvents(
  farmGuid: string | undefined,
  reseedTimeAfter?: string
) {
  return useQuery({
    queryKey: [...queryKeys.events.reseed.all, farmGuid, reseedTimeAfter],
    queryFn: () =>
      reseedEventApi.getAllReseedEventsForFarm(farmGuid ?? "", reseedTimeAfter),
    enabled: !!farmGuid,
    staleTime: EVENT_STALE_TIME,
  });
}

/**
 * Fetch a single reseed event by guid
 */
export function useReseedEvent(guid: string | undefined) {
  return useQuery({
    queryKey: ["reseedEvent", guid],
    queryFn: () => reseedEventApi.getOne(guid ?? ""),
    enabled: !!guid,
    staleTime: EVENT_STALE_TIME,
  });
}

/**
 * Get all reseed events for a specific field
 */
export function useReseedEventsForField(
  farmGuid: string | undefined,
  fieldGuid: string | undefined,
  reseedTimeAfter?: string
) {
  const { data: allEvents, ...rest } = useReseedEvents(
    farmGuid,
    reseedTimeAfter
  );

  const filteredEvents = allEvents?.filter(
    (event) => fieldGuid && event.fieldGuids.includes(fieldGuid)
  );

  return {
    data: filteredEvents,
    ...rest,
  };
}

/**
 * Create a new reseed event
 */
export function useCreateReseedEvent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      reseedEvent,
    }: {
      reseedEvent: ReseedEvent;
      farmGuid: string;
    }) => {
      const result = await reseedEventApi.createOne(reseedEvent);
      return { ...reseedEvent, ...result };
    },
    onSuccess: async (_, { reseedEvent }) => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.events.reseed.all,
      });
      await queryClient.invalidateQueries({
        queryKey: ["autoEvidence", "reseeding"],
      });
      await Promise.all(
        reseedEvent.fieldGuids.map((fieldGuid) =>
          queryClient.invalidateQueries({
            queryKey: queryKeys.fields.detail(fieldGuid),
          })
        )
      );
    },
  });
}

/**
 * Create a reseed event for farm (batch endpoint)
 */
export function useCreateReseedEventForFarm() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      reseedEvent,
    }: {
      reseedEvent: ReseedEventBase;
      farmGuid: string;
    }) => {
      return await reseedEventApi.createReseedsEventForFarm(reseedEvent);
    },
    onSuccess: async (_, { reseedEvent }) => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.events.reseed.all,
      });
      await queryClient.invalidateQueries({
        queryKey: ["autoEvidence", "reseeding"],
      });
      await Promise.all(
        reseedEvent.fieldGuids.map((fieldGuid) =>
          queryClient.invalidateQueries({
            queryKey: queryKeys.fields.detail(fieldGuid),
          })
        )
      );
    },
  });
}

/**
 * Update an existing reseed event
 */
export function useUpdateReseedEvent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ reseedEvent }: { reseedEvent: ReseedEvent }) => {
      const result = await reseedEventApi.updateOne(reseedEvent);
      return { ...reseedEvent, ...result };
    },
    onSuccess: async (updatedEvent) => {
      queryClient.setQueryData(
        ["reseedEvent", updatedEvent.guid],
        updatedEvent
      );
      await queryClient.invalidateQueries({
        queryKey: queryKeys.events.reseed.all,
      });
      await queryClient.invalidateQueries({
        queryKey: ["autoEvidence", "reseeding"],
      });
      await Promise.all(
        updatedEvent.fieldGuids.map((fieldGuid) =>
          queryClient.invalidateQueries({
            queryKey: queryKeys.fields.detail(fieldGuid),
          })
        )
      );
    },
  });
}

/**
 * Delete a reseed event
 */
export function useDeleteReseedEvent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (guid: string) => {
      await fetchApi(`reseeds/${guid}`, { method: "DELETE" }, "v2");
      return guid;
    },
    onSuccess: async (guid) => {
      queryClient.removeQueries({ queryKey: ["reseedEvent", guid] });
      await queryClient.invalidateQueries({
        queryKey: queryKeys.events.reseed.all,
      });
      await queryClient.invalidateQueries({
        queryKey: ["autoEvidence", "reseeding"],
      });
    },
  });
}
